import passwordWords from '@/common/data/passwordWords.json';
import scopeAndSequence from '@/common/data/scopeAndSequence.json';
import {
  GAME_SETTINGS_VERSION,
  migrateGameSettings,
} from '@/common/types/migrations/settings';
import studentMigrator, {
  STUDENT_VERSION,
} from '@/common/types/migrations/student';
import { LevelId } from '@/common/types/scopeAndSequence';
import {
  AudioDelay,
  CharacterInventory,
  ControllerType,
  DailyActivitiesState,
  GameSettings,
  LevelProgress,
  Student,
  TutorializationState,
} from '@/common/types/student';
import api from '@/db/apiClient';
import { capitalize } from 'lodash';
import semver from 'semver';

export function getInitialLevelProgress(levelId: LevelId) {
  const initialLevelProgress: LevelProgress = {
    levelId,
    completedPlaythroughs: 0,
    gamesProgress: {},
  };

  const level = scopeAndSequence.levels.find((l) => l.id === levelId);
  if (!level) return initialLevelProgress;

  for (const game of level.games) {
    initialLevelProgress.gamesProgress[game] = {
      timesMastered: 0,
      timesPlayed: 0,
      currentHighestMastery: 0,
      completedAtNodeIndex: -1,
    };
  }

  return initialLevelProgress;
}

export function getStudentHasMasteredLevel(student: Student, level: number) {
  const gamesProgress = student.levelsProgress[level - 1].gamesProgress;
  console.log(
    'student has mastered level:',
    !Object.values(gamesProgress).find(
      (game) => game.currentHighestMastery < 90,
    ),
  );
  return !Object.values(gamesProgress).find(
    (game) => game.currentHighestMastery < 90,
  );
}

export function getNewDailyActivities(settings: GameSettings) {
  if (settings.duelsEnabled === undefined) settings.duelsEnabled = false;
  const shouldCompleteDuel = settings.duelsEnabled;

  const numMinigames = shouldCompleteDuel ? 3 : 4;

  return {
    ...defaultDailyActivitiesState,
    minigames: {
      numCompletedMinigames: 0,
      targetNumMinigames: numMinigames,
      numRewardsClaimed: 0,
    },
    duel: {
      shouldCompleteDuel: shouldCompleteDuel,
      hasCompletedDuel: false,
      hasClaimedDuelReward: false,
    },
    lastRefresh: Date.now(),
  };
}

const coins = import.meta.env.MODE === 'development' ? 100000 : 100;

export const initialGameSettings: GameSettings = {
  version: GAME_SETTINGS_VERSION,
  playPhonemeOnCardDrop: false,
  wordLimit: 10,
  controllerType: ControllerType.CLICK,
  audio: {
    masterVolume: 100,
    sfxVolume: 100,
    instructionsVolume: 100,
    musicOn: true,
    sfxOn: true,
    audioDelay: AudioDelay.SHORT,
  },
  voice: {
    accent: 'AUS',
    gender: 'male',
  },
  duelsEnabled: true,
  distractorCardsEnabled: true,
  wordSegmentationEnabled: false,
  articulatoryGesturesEnabled: false,
};

export const defaultCharacterInventoryState: CharacterInventory = {
  coins,
  unlockedItems: [
    0, 2, 3, 4, 7, 10, 8, 11, 12, 13, 14, 24, 25, 162, 163, 164, 165, 166, 167,
    182, 183,
  ],
  equippedItems: {
    hats: null,
    clothes: 0,
    pants: 3,
    shoes: 10,
    hair: 2,
    complexion: 98,
    face: null,
    gloves: null,
    back: null,
  },
  newItems: [],
};

export const defaultDailyActivitiesState: DailyActivitiesState = {
  minigames: {
    numCompletedMinigames: 0,
    targetNumMinigames: 3,
    numRewardsClaimed: 0,
  },
  duel: {
    shouldCompleteDuel: true,
    hasCompletedDuel: false,
    hasClaimedDuelReward: false,
  },
  hasClaimedDailyReward: false,
  hasViewedDailyActivities: false,
  lastRefresh: 0,
};

export const defaultTutorializationState: TutorializationState = {
  instructionsViewed: [],
  biomesVisited: [],
  hasViewedHudWalkthrough: false,
  hasViewedDuelWalkthrough: false,
};

export const initialStudent: Omit<
  Student,
  | 'id'
  | 'studentFirstName'
  | 'studentSurname'
  | 'spellcastUsername'
  | 'password'
  | 'createdAt'
  | 'updatedAt'
  | 'createdBy'
  | 'organisationId'
> = {
  version: STUDENT_VERSION,
  level: 1,
  onboardingCompleted: false,
  levelsProgress: scopeAndSequence.levels.map((l) =>
    getInitialLevelProgress(l.id as LevelId),
  ),
  characterInventory: defaultCharacterInventoryState,
  scopeAndSequenceVersion: scopeAndSequence.version,
  settings: initialGameSettings,
  mapsCompleted: 0,
  lastMasteredGame: null,
  assessmentId: null,
  customLevelId: null,
  dailyActivities: defaultDailyActivitiesState,
  tutorialization: defaultTutorializationState,
  lastLogin: 0,
};

function updateStudentScopeAndSequence(student: Student): Student {
  const updatedStudent = structuredClone(student);
  const scopeAndSequenceVersion = scopeAndSequence.version;
  let studentScopeAndSequenceVersion = student.scopeAndSequenceVersion;

  // Convert to semantic versioning if it's a number
  if (typeof studentScopeAndSequenceVersion === 'number') {
    const version = semver.coerce(studentScopeAndSequenceVersion)?.version;
    if (version) {
      studentScopeAndSequenceVersion = version;
    }
  }

  if (semver.lt(studentScopeAndSequenceVersion, scopeAndSequenceVersion)) {
    const currentStudentLevelId =
      student.levelsProgress[student.level - 1].levelId;
    let updatedStudentLevel = student.level;
    const updatedLevelsProgress: LevelProgress[] = [];

    for (let i = 0; i < scopeAndSequence.levels.length; i++) {
      const scopeAndSequenceLevel = scopeAndSequence.levels[i];

      if (scopeAndSequenceLevel.id === currentStudentLevelId) {
        updatedStudentLevel = i + 1;
      }

      const studentLevelProgress = student.levelsProgress.find(
        (level) => level.levelId === scopeAndSequenceLevel.id,
      );

      if (studentLevelProgress) {
        const updatedLevelProgress: LevelProgress = {
          levelId: studentLevelProgress.levelId,
          completedPlaythroughs: studentLevelProgress.completedPlaythroughs,
          gamesProgress: {},
        };
        for (const game of scopeAndSequenceLevel.games) {
          if (studentLevelProgress.gamesProgress[game]) {
            updatedLevelProgress.gamesProgress[game] =
              studentLevelProgress.gamesProgress[game];
          } else {
            updatedLevelProgress.gamesProgress[game] = {
              timesMastered: 0,
              timesPlayed: 0,
              currentHighestMastery: 0,
              completedAtNodeIndex: -1,
            };
          }
        }
        updatedLevelsProgress.push(updatedLevelProgress);
      } else {
        updatedLevelsProgress.push(
          getInitialLevelProgress(scopeAndSequenceLevel.id as LevelId),
        );
      }
    }

    if (updatedLevelsProgress.length !== scopeAndSequence.levels.length) {
      console.error(
        'Error updating student levels progress. Length mismatch:',
        updatedLevelsProgress.length,
        scopeAndSequence.levels.length,
      );
      return student;
    }

    updatedStudent.level = updatedStudentLevel;
    updatedStudent.levelsProgress = updatedLevelsProgress;
    updatedStudent.scopeAndSequenceVersion = scopeAndSequenceVersion;
  }

  return updatedStudent;
}

export async function getUpdatedStudent(
  student: Student,
  apiActor = api.asEducator,
): Promise<Student> {
  let updatedStudent = structuredClone(student);

  // Ensure levels progress is array
  let levelsProgressWasFixed = false;
  if (!Array.isArray(updatedStudent.levelsProgress)) {
    console.warn(
      'levelsProgress was not an array for student',
      updatedStudent.id,
    );
    updatedStudent.levelsProgress = Object.values(
      updatedStudent.levelsProgress,
    );
    levelsProgressWasFixed = true;
  }

  // Update student structure
  updatedStudent = await studentMigrator.migrate(updatedStudent);

  // Update student's settings
  updatedStudent.settings = await migrateGameSettings(updatedStudent.settings);

  // Update student's scope and sequence version
  updatedStudent = updateStudentScopeAndSequence(updatedStudent);

  // Update the student in the database if the version has changed
  if (
    student.version !== updatedStudent.version ||
    student.scopeAndSequenceVersion !==
      updatedStudent.scopeAndSequenceVersion ||
    levelsProgressWasFixed
  ) {
    const { id, ...updatedStudentData } = updatedStudent;
    await apiActor.updateStudentDetails(id, updatedStudentData);
  }

  return updatedStudent;
}

function randInt(max: number): number {
  if (max <= 0) throw new Error('max must be > 0');
  const arr = new Uint32Array(1);
  const limit = Math.floor(0x1_0000_0000 / max) * max;
  let x: number;
  do {
    crypto.getRandomValues(arr);
    x = arr[0];
  } while (x >= limit);
  return x % max;
}

export function generateStudentPassword(): string {
  const n = passwordWords.length;
  if (n < 2) throw new Error('Need at least 2 words');

  const i1 = randInt(n);
  // pick a different index without building a new array
  const i2 = (i1 + 1 + randInt(n - 1)) % n;

  const number = randInt(100).toString().padStart(2, '0'); // 00–99

  return capitalize(passwordWords[i1]) + capitalize(passwordWords[i2]) + number;
}

export function studentIsDev(student: Student) {
  return student.dev ?? false;
}

const USERNAME_REGEX = /^[a-z0-9-]{1,20}$/;

export function sanitizeUsername(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '')
    .slice(0, 20);
}

export function isValidUsername(username: string): boolean {
  return USERNAME_REGEX.test(username);
}
